import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setTenantOccupancy } from "state";
import { useAnonymousTenancies } from "services/hooks/rentals";
import AnonymousTenanciesTable from "components/Tables/AnonymousTenanciesTable";
import EditAnonymousTenancy from "components/AnonymousTenancyModals/EditAnonymousTenancy";
import * as XLSX from "xlsx";

const isBankMode = (paymentMode = "") => paymentMode.toUpperCase() === "BANK";

const isMobileMoneyMode = (paymentMode = "") => {
  const mode = paymentMode.toUpperCase();
  return mode.includes("MOBILE");
};

const formatCurrencyForExport = (amount, currency = "UGX") => {
  if (typeof amount !== "number") return `0 ${currency}`;
  return `${new Intl.NumberFormat("en-UG", { maximumFractionDigits: 0 }).format(amount)} ${currency}`;
};

const getPayoutInstructionForExport = (landlord = {}) => {
  if (!landlord.payment_mode) return "Not set";

  if (isBankMode(landlord.payment_mode)) {
    return `${landlord.bank_name || "No bank"} - ${landlord.account_number || "No account"}`;
  }

  return `${landlord.payment_mode} - ${landlord.mobile_number || "No mobile number"}`;
};

const buildTenantOccupancyFromRow = (row) => ({
  id: row?.occupancy?.id,
  date_added: row?.occupancy?.date_added,
  related_tenant: {
    id: row?.tenant?.id,
    first_name: row?.tenant?.first_name,
    last_name: row?.tenant?.last_name,
    tenant_number: row?.tenant?.tenant_number,
    phone_number: row?.tenant?.phone_number,
  },
  related_rental_unit: {
    id: row?.unit?.id,
    unit_name: row?.unit?.name,
    related_rental: {
      id: row?.property?.id,
      rental_name: row?.property?.name,
    },
  },
});

const AnonymousTenancies = () => {
  const roleFromState = useSelector((state) => state.role);
  const isAdminFlag = useSelector((state) => state.isAdmin);
  const role = roleFromState || (isAdminFlag === 1 ? "admin" : "manager");
  const isManager = role === "manager";
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [paymentInstructionFilter, setPaymentInstructionFilter] = useState("all");
  const [dueStatusFilter, setDueStatusFilter] = useState("all");
  const [selectedCycleIds, setSelectedCycleIds] = useState([]);
  const [payoutDescription, setPayoutDescription] = useState("Batch payout for anonymous landlord tenancies");
  const [actionMessage, setActionMessage] = useState({ type: "", text: "" });
  const [editingRow, setEditingRow] = useState(null);
  const [editError, setEditError] = useState("");

  const {
    anonymousTenancies,
    count,
    isLoadingAnonymousTenancies,
    errorAnonymousTenancies,
    isMarkingAnonymousTenanciesPaid,
    markAnonymousTenanciesPaidError,
    markAnonymousTenanciesPaidMessage,
    markAnonymousTenanciesPaid,
    isUpdatingAnonymousTenancy,
    updateAnonymousTenancyError,
    updateAnonymousTenancy,
  } = useAnonymousTenancies();

  const filteredData = useMemo(() => {
    const query = search.trim().toLowerCase();

    const filtered = anonymousTenancies.filter((item) => {
      const paymentMode = item?.landlord?.payment_mode || "";
      const dueStatus = (item?.billing?.due_status || "unknown").toLowerCase();

      if (paymentInstructionFilter === "bank" && !isBankMode(paymentMode)) return false;
      if (paymentInstructionFilter === "mobile_money" && !isMobileMoneyMode(paymentMode)) return false;
      if (dueStatusFilter !== "all" && dueStatus !== dueStatusFilter) return false;

      if (!query) return true;

      const tenantName = `${item?.tenant?.first_name || ""} ${item?.tenant?.last_name || ""}`.toLowerCase();
      const tenantNumber = (item?.tenant?.tenant_number || "").toLowerCase();
      const propertyName = (item?.property?.name || "").toLowerCase();
      const unitName = (item?.unit?.name || "").toLowerCase();
      const landlordName = (item?.landlord?.name || "").toLowerCase();
      return (
        tenantName.includes(query) ||
        tenantNumber.includes(query) ||
        propertyName.includes(query) ||
        unitName.includes(query) ||
        landlordName.includes(query)
      );
    });

    const orderDueStatus = (status) => {
      switch (status) {
        case "overdue":
          return 0;
        case "due_today":
          return 1;
        case "upcoming":
          return 2;
        default:
          return 3;
      }
    };

    const sorted = [...filtered].sort((a, b) => {
      const aCycle = a?.payout_cycle || {};
      const bCycle = b?.payout_cycle || {};
      const aPayable = Boolean(aCycle.can_mark_paid);
      const bPayable = Boolean(bCycle.can_mark_paid);
      if (aPayable !== bPayable) {
        return aPayable ? -1 : 1; // PAYABLE first
      }

      const aDueStatus = (a?.billing?.due_status || "unknown").toLowerCase();
      const bDueStatus = (b?.billing?.due_status || "unknown").toLowerCase();
      const dueOrderDiff = orderDueStatus(aDueStatus) - orderDueStatus(bDueStatus);
      if (dueOrderDiff !== 0) return dueOrderDiff;

      const aDueDate = a?.billing?.due_date || "";
      const bDueDate = b?.billing?.due_date || "";
      return (aDueDate || "").localeCompare(bDueDate || "");
    });

    return sorted;
  }, [anonymousTenancies, search, paymentInstructionFilter, dueStatusFilter]);

  const selectableCycleIds = useMemo(
    () =>
      filteredData
        .filter((item) => item?.payout_cycle?.id)
        .map((item) => item.payout_cycle.id),
    [filteredData]
  );

  useEffect(() => {
    setSelectedCycleIds((current) => current.filter((id) => selectableCycleIds.includes(id)));
  }, [selectableCycleIds]);

  const handleToggleCycle = (cycleId) => {
    setSelectedCycleIds((current) =>
      current.includes(cycleId) ? current.filter((id) => id !== cycleId) : [...current, cycleId]
    );
  };

  const handleToggleAllPayable = () => {
    setSelectedCycleIds((current) => {
      const allSelected = selectableCycleIds.length > 0 && selectableCycleIds.every((id) => current.includes(id));
      if (allSelected) return current.filter((id) => !selectableCycleIds.includes(id));
      const merged = new Set([...current, ...selectableCycleIds]);
      return Array.from(merged);
    });
  };

  const handleMarkSelectedPaid = async () => {
    setActionMessage({ type: "", text: "" });
    if (selectedCycleIds.length === 0) {
      setActionMessage({ type: "error", text: "Select at least one payable cycle." });
      return;
    }

    const shouldProceed = window.confirm(`Mark ${selectedCycleIds.length} payout cycle(s) as paid?`);
    if (!shouldProceed) return;

    const result = await markAnonymousTenanciesPaid(selectedCycleIds, payoutDescription);
    if (result.success) {
      setActionMessage({
        type: "success",
        text: result?.data?.message || `Marked ${selectedCycleIds.length} cycle(s) as paid successfully.`,
      });
      setSelectedCycleIds([]);
    } else {
      setActionMessage({ type: "error", text: result.message || "Failed to mark selected cycles as paid." });
    }
  };

  const handleNavigateTenantPayments = (row) => {
    if (!row?.occupancy?.id) return;
    dispatch(setTenantOccupancy(buildTenantOccupancyFromRow(row)));
    navigate("/tenant-payments");
  };

  const handleOpenEdit = (row) => {
    setEditError("");
    setEditingRow(row);
  };

  const handleCloseEdit = () => {
    if (isUpdatingAnonymousTenancy) return;
    setEditingRow(null);
    setEditError("");
  };

  const handleSaveEdit = async (formValues) => {
    setEditError("");
    const occupancyId = editingRow?.occupancy?.id;
    if (!occupancyId) return;

    const payload = Object.fromEntries(
      Object.entries(formValues).filter(([, value]) => value !== "" && value != null)
    );

    const result = await updateAnonymousTenancy(occupancyId, payload);
    if (result.success) {
      setActionMessage({ type: "success", text: "Anonymous tenancy updated successfully." });
      setEditingRow(null);
      return;
    }

    const message =
      typeof result.message === "string"
        ? result.message
        : "Failed to update anonymous tenancy.";
    setEditError(message);
  };

  const handleDownloadExcel = () => {
    const rows = [
      ["Anonymous Landlord Tenancies"],
      [],
      ["Filters"],
      ["Search", "Payment Instruction"],
      [search || "N/A", paymentInstructionFilter === "all" ? "All" : paymentInstructionFilter],
      [],
      [
        "Tenant Number",
        "Tenant Name",
        "Property",
        "Unit",
        "Due Date",
        "Occupancy Date",
        "Payout Cycle ID",
        "Payout Cycle Sequence",
        "Payout Cycle Status",
        "Can Mark Paid",
        "Due Status",
        "Amount Due",
        "Amount Paid In Cycle",
        "Amount Paid So Far",
        "Paid To Landlord",
        "Landlord Name",
        "Payment Mode",
        "Payout Instruction",
      ],
    ];

    filteredData.forEach((item) => {
      const tenantName = `${item?.tenant?.first_name || ""} ${item?.tenant?.last_name || ""}`.trim();
      rows.push([
        item?.tenant?.tenant_number || "",
        tenantName || "",
        item?.property?.name || "",
        item?.unit?.name || "",
        item?.billing?.due_date || "",
        item?.occupancy?.date_added || "",
        item?.payout_cycle?.id || "",
        item?.payout_cycle?.sequence || "",
        item?.payout_cycle?.status || "",
        item?.payout_cycle?.can_mark_paid ? "YES" : "NO",
        item?.billing?.due_status || "unknown",
        formatCurrencyForExport(item?.billing?.amount_due, item?.billing?.currency),
        formatCurrencyForExport(item?.payout_cycle?.cycle_paid_amount, item?.billing?.currency),
        formatCurrencyForExport(item?.billing?.amount_paid_so_far, item?.billing?.currency),
        formatCurrencyForExport(item?.landlord?.paid_to_landlord_total, item?.billing?.currency),
        item?.landlord?.name || "",
        item?.landlord?.payment_mode || "",
        getPayoutInstructionForExport(item?.landlord),
      ]);
    });

    const ws = XLSX.utils.aoa_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Anonymous Tenancies");
    const fileName = `anonymous-tenancies-${paymentInstructionFilter}-${filteredData.length}.xlsx`;
    XLSX.writeFile(wb, fileName);
  };

  if (!isManager) {
    return (
      <Box m={2}>
        <Alert severity="warning">Only Property Managers can access Anonymous Tenancies.</Alert>
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Typography variant="h4" sx={{ fontWeight: "bold", mb: 1 }}>
        Anonymous Landlord Tenancies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Active occupancies where landlord payout can only proceed after anonymous onboarding is completed.
      </Typography>
      <Typography variant="body2" sx={{ mb: 2 }}>
        Showing {filteredData.length} of {count} tenancy record(s).
      </Typography>
      {actionMessage.text && (
        <Alert severity={actionMessage.type === "error" ? "error" : "success"} sx={{ mb: 2 }}>
          {actionMessage.text}
        </Alert>
      )}
      {markAnonymousTenanciesPaidError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {markAnonymousTenanciesPaidError}
        </Alert>
      )}
      {markAnonymousTenanciesPaidMessage && !actionMessage.text && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {markAnonymousTenanciesPaidMessage}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={5}>
          <TextField
            label="Search tenant, landlord, property or unit"
            variant="outlined"
            fullWidth
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <FormControl fullWidth>
            <InputLabel>Payment Instruction</InputLabel>
            <Select
              label="Payment Instruction"
              value={paymentInstructionFilter}
              onChange={(event) => setPaymentInstructionFilter(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="bank">Bank</MenuItem>
              <MenuItem value="mobile_money">Mobile Money</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2}>
          <FormControl fullWidth>
            <InputLabel>Due Status</InputLabel>
            <Select
              label="Due Status"
              value={dueStatusFilter}
              onChange={(event) => setDueStatusFilter(event.target.value)}
            >
              <MenuItem value="all">All</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="due_today">Due today</MenuItem>
              <MenuItem value="upcoming">Upcoming</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={2} display="flex" alignItems="center">
          <Button
            variant="contained"
            onClick={handleDownloadExcel}
            disabled={filteredData.length === 0}
            fullWidth
          >
            Download Excel
          </Button>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={8}>
          <TextField
            label="Mark-paid description"
            variant="outlined"
            fullWidth
            value={payoutDescription}
            onChange={(event) => setPayoutDescription(event.target.value)}
          />
        </Grid>
        <Grid item xs={12} md={4} display="flex" alignItems="center">
          <Button
            variant="contained"
            color="success"
            onClick={handleMarkSelectedPaid}
            disabled={selectedCycleIds.length === 0 || isMarkingAnonymousTenanciesPaid}
            fullWidth
          >
            {isMarkingAnonymousTenanciesPaid
              ? "Marking selected cycles..."
              : `Mark Selected Paid (${selectedCycleIds.length})`}
          </Button>
        </Grid>
      </Grid>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Select rows with available cycles to process bulk landlord payouts.
      </Typography>

      <AnonymousTenanciesTable
        data={filteredData}
        loading={isLoadingAnonymousTenancies}
        error={errorAnonymousTenancies}
        selectedCycleIds={selectedCycleIds}
        selectableCycleIds={selectableCycleIds}
        onToggleCycle={handleToggleCycle}
        onToggleAllSelectable={handleToggleAllPayable}
        onNavigateTenantPayments={handleNavigateTenantPayments}
        onEditRow={handleOpenEdit}
      />

      <EditAnonymousTenancy
        open={Boolean(editingRow)}
        row={editingRow}
        onClose={handleCloseEdit}
        onSave={handleSaveEdit}
        isSaving={isUpdatingAnonymousTenancy}
        errorMessage={editError || updateAnonymousTenancyError}
      />
    </Box>
  );
};

export default AnonymousTenancies;
